import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import { fetchWrapper } from '../utils';
import { API_URL, DEFAULT_STRIPE_PK, DEFAULT_STRIPE_NAME } from '../constants';
import { setShowLoader } from './loader';
import { setShowError } from './error';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import { queryParser } from '@applyft-web/utils';
import { setEventData } from './events';
import {
  setAmplitudeUserProperties,
  setAnalyticsUserId,
  type GoogleAdsGeoData,
} from '../../analytics';

export interface UserGeo {
  city: string | null;
  region: string | null;
}

export const signupSlice = createSlice({
  name: 'signup',
  initialState: {
    uuid: '',
    email: '',
    emailValidity: false,
    token: '',
    btToken: '',
    emailConsent: false,
    deeplinkToken: '',
    deeplinkUrl: '',
    deeplinkQR: '',
    userId: '',
    isSuccess: false,
    stripe: {
      key: null,
      name: null,
    },
    pixAutomatico: {
      key: null,
      accountName: null,
    },
    upi: {
      key: null,
      accountName: null,
    },
    blik: {
      key: null,
      accountName: null,
    },
    amazonPay: {
      key: null,
      accountName: null,
    },
    cashApp: {
      key: null,
      accountName: null,
    },
    paymentProvider: null,
    paymentSettingsStatus: null,
    threeDS: 0,
    userLocation: undefined as string | null | undefined,
    userGeo: null as UserGeo | null,
  },
  reducers: {
    setUserUuid: (state, action: PayloadAction<string>) => {
      state.uuid = action.payload;
    },
    setEmail: (state, action) => {
      const { email, isValid } = action.payload;
      state.email = email;
      state.emailValidity = isValid;
    },
    setEmailConsent: (state, action: PayloadAction<boolean>) => {
      state.emailConsent = action.payload;
    },
    setRequestResult: (state, action) => {
      const {
        access_token,
        deeplink_token,
        entity: { external_user_id },
      } = action.payload;
      state.isSuccess = true;
      state.token = access_token;
      state.deeplinkToken = deeplink_token;
      state.userId = external_user_id;
    },
    setUserToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setPaymentSettingStatus: (state, action) => {
      state.paymentSettingsStatus = action.payload;
    },
    setStripeData: (state, action) => {
      state.stripe = action.payload;
    },
    setPixAutomatico: (state, action) => {
      state.pixAutomatico = action.payload;
    },
    setUpi: (state, action) => {
      state.upi = action.payload;
    },
    setBlik: (state, action) => {
      state.blik = action.payload;
    },
    setAmazonPay: (state, action) => {
      state.amazonPay = action.payload;
    },
    setCashApp: (state, action) => {
      state.cashApp = action.payload;
    },
    setBtToken: (state, action: PayloadAction<string>) => {
      state.btToken = action.payload;
    },
    setPaymentProvider: (state, action) => {
      state.paymentProvider = action.payload;
    },
    setThreeDS: (state, action: PayloadAction<number>) => {
      state.threeDS = action.payload;
    },
    setUserLocation: (state, action) => {
      state.userLocation = action.payload;
    },
    setUserGeo: (state, action: PayloadAction<UserGeo | null>) => {
      state.userGeo = action.payload;
    },
    setDeeplinkData: (state, action) => {
      const { short_deeplink, qr_code } = action.payload;
      state.deeplinkUrl = short_deeplink;
      state.deeplinkQR = qr_code;
    },
  },
});

export const {
  setUserUuid,
  setUserToken,
  setEmail,
  setEmailConsent,
  setRequestResult,
  setPaymentSettingStatus,
  setPaymentProvider,
  setPixAutomatico,
  setUpi,
  setBlik,
  setAmazonPay,
  setCashApp,
  setStripeData,
  setBtToken,
  setThreeDS,
  setUserLocation,
  setUserGeo,
  setDeeplinkData,
} = signupSlice.actions;

interface SignupParams {
  readonly email: string;
  readonly user_uuid: string;
  readonly landing_type: string;
  readonly name?: string;
  readonly consent_email: boolean;
}

interface SignupTokenParams {
  readonly token: string;
  readonly user_uuid: string;
  readonly landing_type: string;
  readonly onSuccess?: (email: string) => void;
  readonly consent_email: boolean;
}

export const sendSignupRequest =
  ({ email, name, landing_type, user_uuid, consent_email }: SignupParams) =>
  (dispatch: Dispatch) => {
    dispatch(setShowLoader(true));

    const params = {
      registration_source: 'email',
      name: name ?? email,
      email,
      landing_type,
      user_uuid,
      consent_email,
    };

    fetchWrapper(`${API_URL}/signup`, { body: params })
      .then((data) => {
        window.sessionStorage.setItem('access_token', data.data.access_token);
        dispatch(setRequestResult(data.data));
        dispatch(getUserLocation());
        getPaymentSettings(data.data.access_token)(dispatch);
        getDeeplinkData(data.data.access_token)(dispatch);
        setAnalyticsUserId(data.data.entity.external_user_id);
      })
      .catch((error) => {
        dispatch(setShowError(error.errors?.messages || 'Failed to create an account'));
        dispatch(setShowLoader(false));
      });
  };

export const sendSignupTokenRequest =
  ({ token, user_uuid, landing_type, onSuccess, consent_email }: SignupTokenParams) =>
  (dispatch: Dispatch) => {
    dispatch(setShowLoader(true));

    const params = {
      access_token: token,
      driver: 'google',
      landing_type,
      user_uuid,
      consent_email,
    };

    fetchWrapper(`${API_URL}/authorize_social_via_access_token`, { body: params })
      .then((data) => {
        window.sessionStorage.setItem('access_token', data.data.access_token);
        dispatch(setRequestResult(data.data));
        getPaymentSettings(data.data.access_token)(dispatch);
        dispatch(setEmail({ email: data.data.entity?.email, isValid: true }));
        getDeeplinkData(data.data.access_token)(dispatch);
        setAnalyticsUserId(data.data.entity.external_user_id);
        if (onSuccess && data?.data?.entity?.email) {
          onSuccess(data.data.entity.email);
        }
      })
      .catch((error) => {
        dispatch(setShowError(error.errors?.messages || 'Failed to create an account'));
        dispatch(setShowLoader(false));
      });
  };

export const getUserLocation = (): any => (dispatch: Dispatch) => {
  const { custom_geo } = queryParser(window.location.search);
  if (custom_geo) {
    dispatch(setUserLocation(custom_geo));
  } else {
    fetchWrapper(`${API_URL}/cf-country`)
      .then((data) => {
        dispatch(setUserLocation(data?.country_code || null));
        dispatch(
          setUserGeo({
            city: data?.city || null,
            region: data?.region_code || null,
          }),
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }
};

export const getDeeplinkData = (token: string) => (dispatch: Dispatch) => {
  fetchWrapper(`${API_URL}/users/me/deeplink-qr-code`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((data) => {
      dispatch(setDeeplinkData(data.data));
    })
    .catch((error) => {
      console.error(error);
    });
};

const ALTERNATE_METHOD_SETTINGS = [
  { apiKey: 'pix_automatico', setSettings: setPixAutomatico },
  { apiKey: 'upi', setSettings: setUpi },
  { apiKey: 'blik', setSettings: setBlik },
  { apiKey: 'cashapp', setSettings: setCashApp },
  { apiKey: 'amazon_pay', setSettings: setAmazonPay },
] as const;

export const getPaymentSettings =
  (token: string, attemptNum: number = 0) =>
  (dispatch: Dispatch) => {
    let stripeParams: { [key: string]: string } = {
        key: DEFAULT_STRIPE_PK,
        name: DEFAULT_STRIPE_NAME,
      },
      status: null | string = null,
      paymentGateway: null | string = null;

    fetchWrapper(`${API_URL}/users/me/payments-settings`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((data) => {
        const {
          stripe_publishable_key,
          stripe_account_name,
          braintree_client_token,
          payment_gateway,
          request_three_d_secure,
          payment_methods,
        } = data;
        paymentGateway = payment_gateway || 'stripe';
        ALTERNATE_METHOD_SETTINGS.forEach(({ apiKey, setSettings }) => {
          const settings = payment_methods?.[apiKey];

          dispatch(
            settings?.stripe_publishable_key
              ? setSettings({
                  key: settings.stripe_publishable_key,
                  accountName: settings.account_name || settings.stripe_account_name || null,
                })
              : setSettings({ key: null, accountName: null }),
          );
        });
        dispatch(setBtToken(braintree_client_token || null));
        dispatch(setThreeDS(request_three_d_secure || 0));
        status = 'success';
        if (stripe_publishable_key) {
          stripeParams = {
            key: stripe_publishable_key,
            name: stripe_account_name,
          };
        }
      })
      .catch((error) => {
        if (attemptNum < 2) {
          setTimeout(() => getPaymentSettings(token, ++attemptNum)(dispatch), 1000);
        } else {
          status = 'fail';
          paymentGateway = 'stripe';
        }
      })
      .then(() => {
        dispatch(setPaymentProvider(paymentGateway));
        dispatch(setStripeData(stripeParams));
        dispatch(setPaymentSettingStatus(status));
      })
      .then(() => dispatch(setShowLoader(false)));
  };

export const setUserEmailConsent = (token: string | undefined, consentEmail: boolean) => {
  const authToken = token || window.sessionStorage.getItem('access_token') || '';
  if (!authToken) {
    return Promise.resolve();
  }
  return fetchWrapper(`${API_URL}/users/me/email-consent`, {
    body: {
      consent: consentEmail,
    },
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  }).catch((error) => {
    console.error(error);
  });
};

export const getUserInfo = (token: string) => (dispatch: Dispatch) => {
  fetchWrapper(`${API_URL}/users/me`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((data) => {
      window.sessionStorage.setItem('access_token', token);
      dispatch(setRequestResult({ ...data.data, access_token: token }));
      dispatch(setEmail({ email: data.data.entity.email, isValid: true }));
      getPaymentSettings(token)(dispatch);
      getDeeplinkData(token)(dispatch);
      setAnalyticsUserId(data.data.entity.external_user_id);
      setUuid(data.data.entity.uuid)(dispatch);
    })
    .catch((error) => console.error(error));
};

export const setUuid =
  (userUuid: string = '') =>
  (dispatch: Dispatch) => {
    const cookieUuid = Cookies.get('userUuid');
    const generatedUuid = uuidv4();
    const uuid = userUuid || cookieUuid || generatedUuid;

    if (!cookieUuid) Cookies.set('userUuid', uuid, { expires: 365 });
    dispatch(setUserUuid(uuid));
    dispatch(setEventData({ uuid }));
    setAmplitudeUserProperties({ uuid });
  };

export const selectIsSuccess = (state: any) => state.signup.isSuccess;
export const selectPaymentSettingsStatus = (state: any) => state.signup.paymentSettingsStatus;
export const selectEmail = (state: any) => state.signup.email;
export const selectEmailConsent = (state: any) => state.signup.emailConsent;
export const selectEmailValidity = (state: any) => state.signup.emailValidity;
export const selectUuid = (state: any) => state.signup.uuid;
export const selectToken = (state: any) => state.signup.token;
export const selectPaymentProvider = (state: any) => state.signup.paymentProvider;
export const selectStripe = (state: any) => state.signup.stripe;
export const selectPixAutomatico = (state: any) => state.signup.pixAutomatico;
export const selectUpi = (state: any) => state.signup.upi;
export const selectBlik = (state: any) => state.signup.blik;
export const selectAmazonPay = (state: any) => state.signup.amazonPay;
export const selectCashApp = (state: any) => state.signup.cashApp;
export const selectBtToken = (state: any) => state.signup.btToken;
export const selectDeeplinkToken = (state: any) => state.signup.deeplinkToken;
export const selectDeeplinkUrl = (state: any) => state.signup.deeplinkUrl;
export const selectDeeplinkQR = (state: any) => state.signup.deeplinkQR;
export const selectThreeDS = (state: any) => state.signup.threeDS;
export const selectUserLocation = (state: any) => state.signup.userLocation;
export const selectGoogleAdsGeo = (state: any): GoogleAdsGeoData => ({
  country: state.signup.userLocation,
  city: state.signup.userGeo?.city,
  region: state.signup.userGeo?.region,
});

export default signupSlice.reducer;
